import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { purchaseRequestApi } from '../api';
import { PurchaseRequest, PurchaseRequestStatus } from '../types';
import { useAuthStore } from '../store/authStore';
import { serviceFeeUsd, SERVICE_FEE_MIN_USD, SERVICE_FEE_RATE } from '../config/pricing';
import ExtraChargePayment from '../components/purchaserequest/ExtraChargePayment';

const STATUS_LABEL: Record<PurchaseRequestStatus, { text: string; color: string }> = {
  REQUESTED: { text: 'Requested', color: '#888' },
  QUOTED: { text: 'Quote Ready', color: '#5c6bc0' },
  PAID: { text: 'Paid', color: '#00897b' },
  APPROVED: { text: 'Approved', color: '#4caf50' },
  REJECTED: { text: 'Rejected', color: '#e53935' },
  CANCELLED: { text: 'Cancelled', color: '#bbb' },
  SHIPPED: { text: 'Shipped', color: '#9c27b0' },
};

const empty = { productUrl: '', productName: '', optionText: '', quantity: 1, budgetUsd: '', memo: '' };

const PurchaseRequestPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ ...empty, productUrl: searchParams.get('url') ?? '' });
  const [list, setList] = useState<PurchaseRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    purchaseRequestApi.getMine()
      .then(r => setList(r.data.data.content))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (user) load(); }, [user]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.productUrl.trim() || !form.productName.trim()) {
      alert('Product URL and name are required.');
      return;
    }
    if (!user) {
      const back = `/purchase-requests?url=${encodeURIComponent(form.productUrl.trim())}`;
      navigate('/login', { state: { from: back } });
      return;
    }
    setSubmitting(true);
    try {
      await purchaseRequestApi.create({
        productUrl: form.productUrl.trim(),
        productName: form.productName.trim(),
        optionText: form.optionText.trim() || undefined,
        quantity: Number(form.quantity) || 1,
        budgetUsd: form.budgetUsd ? Number(form.budgetUsd) : undefined,
        memo: form.memo.trim() || undefined,
      });
      alert('Your request has been submitted.');
      setForm({ ...empty });
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id: number) => {
    if (!window.confirm('Cancel this request?')) return;
    try {
      await purchaseRequestApi.cancel(id);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to cancel');
    }
  };

  const f = (k: keyof typeof form) => ({
    value: form[k] as any,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [k]: e.target.value }),
  });

  return (
    <div style={s.page}>
      <h1 style={s.title}>Item Request</h1>
      <p style={s.sub}>Share the link of the item you want, and we'll review it and send you a quote.</p>
      <p style={s.feeNote}>
        * A service fee of {SERVICE_FEE_RATE * 100}% of the item price applies, with a minimum of ${SERVICE_FEE_MIN_USD}.
      </p>

      <form onSubmit={submit} style={s.card}>
        <div style={s.field}>
          <label style={s.label}>Product URL *</label>
          <input style={s.input} placeholder="https://..." {...f('productUrl')} />
        </div>
        <div style={s.field}>
          <label style={s.label}>Product Name *</label>
          <input style={s.input} placeholder="e.g. Nike Tech Fleece Hoodie" {...f('productName')} />
        </div>
        <div style={s.row}>
          <div style={{ ...s.field, flex: 2 }}>
            <label style={s.label}>Options (color / size)</label>
            <input style={s.input} placeholder="Black / L" {...f('optionText')} />
          </div>
          <div style={{ ...s.field, flex: 1 }}>
            <label style={s.label}>Quantity</label>
            <input style={s.input} type="number" min={1} {...f('quantity')} />
          </div>
          <div style={{ ...s.field, flex: 1 }}>
            <label style={s.label}>Item Price (USD)</label>
            <input style={s.input} type="number" min={0} step="0.01" placeholder="e.g. 70" {...f('budgetUsd')} />
          </div>
        </div>
        {Number(form.budgetUsd) > 0 && (
          <div style={s.feeBreakdown}>
            <span>
              Service fee: <strong>${serviceFeeUsd(Number(form.budgetUsd)).toFixed(2)}</strong>
              {Number(form.budgetUsd) * SERVICE_FEE_RATE < SERVICE_FEE_MIN_USD && (
                <span style={s.feeEst}> (${SERVICE_FEE_MIN_USD} minimum)</span>
              )}
            </span>
            <span style={s.feeEst}>
              Estimated total (excl. shipping): ${(Number(form.budgetUsd) + serviceFeeUsd(Number(form.budgetUsd))).toFixed(2)}
            </span>
          </div>
        )}
        <div style={s.field}>
          <label style={s.label}>Note</label>
          <textarea style={{ ...s.input, height: 70, resize: 'none' }} placeholder="Any requests" {...f('memo')} />
        </div>
        <button type="submit" style={s.submitBtn} disabled={submitting}>
          {submitting ? 'Submitting...' : user ? 'Submit Request' : 'Sign in & submit'}
        </button>
      </form>

      {user && (
      <>
      <h2 style={s.listTitle}>My Requests</h2>
      {loading ? (
        <p style={s.muted}>Loading...</p>
      ) : list.length === 0 ? (
        <p style={s.muted}>No requests yet.</p>
      ) : (
        <div style={s.list}>
          {list.map(r => {
            const st = STATUS_LABEL[r.status];
            return (
              <div key={r.id} style={s.item}>
                <div style={{ flex: 1 }}>
                  <div style={s.itemTop}>
                    <span style={s.itemName}>{r.productName}</span>
                    <span style={{ ...s.badge, color: st.color, borderColor: st.color }}>{st.text}</span>
                  </div>
                  <a href={r.productUrl} target="_blank" rel="noreferrer" style={s.link}>{r.productUrl}</a>
                  <div style={s.metaRow}>
                    {r.optionText && <span>Option: {r.optionText}</span>}
                    <span>Qty: {r.quantity}</span>
                    {r.budgetUsd != null && <span>Budget: ${Number(r.budgetUsd).toFixed(2)}</span>}
                  </div>
                  {(r.status === 'QUOTED' || r.status === 'PAID' || r.status === 'SHIPPED') && r.quotedPriceUsd != null && (
                    <div style={s.quoteBox}>
                      <div style={s.quoteRow}><span>Item price</span><span>${Number(r.itemPriceUsd ?? 0).toFixed(2)}</span></div>
                      <div style={s.quoteRow}><span>Service fee</span><span>${Number(r.serviceFeeUsd ?? 0).toFixed(2)}</span></div>
                      <div style={s.quoteRow}><span>Shipping</span><span>${Number(r.shippingFeeUsd ?? 0).toFixed(2)}</span></div>
                      <div style={{ ...s.quoteRow, ...s.quoteTotal }}><span>Total</span><span>${Number(r.quotedPriceUsd).toFixed(2)}</span></div>
                      {r.adminNote && <div style={s.adminNote}>Note: {r.adminNote}</div>}
                      {(r.status === 'PAID' || r.status === 'SHIPPED') && <div style={s.paidTag}>✓ Paid</div>}
                      {r.status === 'QUOTED' && (
                        <div style={s.payWrap}>
                          <PayPalButtons
                            style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay', height: 40 }}
                            createOrder={async () => {
                              const res = await purchaseRequestApi.createPayPalOrder(r.id);
                              return res.data.data.paypalOrderId;
                            }}
                            onApprove={async (data) => {
                              try {
                                await purchaseRequestApi.capturePayPal(r.id, data.orderID as string);
                                alert('Payment complete!');
                                load();
                              } catch (err: any) {
                                alert(err.response?.data?.message || 'Payment failed');
                              }
                            }}
                            onError={() => alert('PayPal error')}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  <ExtraChargePayment request={r} onPaid={load} />
                  {r.trackingNumber && (
                    <div style={s.trackBox}>
                      <div>🚚 Tracking: <strong>{r.trackingNumber}</strong></div>
                      {r.packingVideoUrl && (
                        <a href={r.packingVideoUrl} target="_blank" rel="noreferrer" style={s.videoLink}>📦 Packing video ↗</a>
                      )}
                    </div>
                  )}
                  {r.status === 'REJECTED' && r.adminNote && (
                    <div style={s.rejectBox}>Rejected: {r.adminNote}</div>
                  )}
                </div>
                {(r.status === 'REQUESTED' || r.status === 'QUOTED') && (
                  <button style={s.cancelBtn} onClick={() => cancel(r.id)}>Cancel</button>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 760, margin: '40px auto', padding: '0 20px' },
  title: { fontSize: 24, fontWeight: 700, marginBottom: 6 },
  sub: { color: '#888', fontSize: 14, marginBottom: 6 },
  feeNote: { color: '#aaa', fontSize: 12, marginBottom: 24 },
  card: { background: '#fff', border: '1px solid #f0f0f0', borderRadius: 12, padding: 24, display: 'flex', flexDirection: 'column', gap: 16 },
  row: { display: 'flex', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: '#555' },
  input: { padding: '12px 14px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  feeBreakdown: { display: 'flex', flexDirection: 'column', gap: 2, marginTop: -6, padding: '8px 12px', background: '#fafafa', borderRadius: 8, fontSize: 13, color: '#555' },
  feeEst: { fontSize: 12, color: '#aaa' },
  submitBtn: { padding: 14, background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  listTitle: { fontSize: 18, fontWeight: 700, margin: '36px 0 16px' },
  muted: { color: '#bbb', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  item: { display: 'flex', gap: 12, alignItems: 'flex-start', background: '#fff', border: '1px solid #f0f0f0', borderRadius: 10, padding: 16 },
  itemTop: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 },
  itemName: { fontSize: 15, fontWeight: 600 },
  badge: { fontSize: 11, fontWeight: 700, padding: '2px 8px', border: '1px solid', borderRadius: 10 },
  link: { fontSize: 12, color: '#5c6bc0', wordBreak: 'break-all' },
  metaRow: { display: 'flex', gap: 14, fontSize: 12, color: '#888', marginTop: 6 },
  quoteBox: { marginTop: 8, padding: '10px 12px', background: '#f5f9ff', border: '1px solid #d6e4f5', borderRadius: 8, fontSize: 13, color: '#1a5fa8' },
  quoteRow: { display: 'flex', justifyContent: 'space-between', padding: '2px 0' },
  quoteTotal: { borderTop: '1px solid #d6e4f5', marginTop: 4, paddingTop: 6, fontWeight: 700 },
  paidTag: { marginTop: 8, color: '#2e7d32', fontWeight: 700 },
  rejectBox: { marginTop: 8, padding: '8px 12px', background: '#fff4f4', border: '1px solid #f5c6c6', borderRadius: 8, fontSize: 13, color: '#c62828' },
  payWrap: { marginTop: 10 },
  adminNote: { color: '#555', marginTop: 6, fontSize: 12 },
  trackBox: { marginTop: 10, padding: '10px 12px', background: '#f6f0fb', border: '1px solid #e3d4f2', borderRadius: 8, fontSize: 13, color: '#6a1b9a' },
  videoLink: { display: 'inline-block', marginTop: 6, color: '#6a1b9a', fontWeight: 600, textDecoration: 'none', fontSize: 12 },
  cancelBtn: { padding: '8px 14px', background: '#fff', color: '#e53935', border: '1px solid #e53935', borderRadius: 8, fontSize: 13, cursor: 'pointer', flexShrink: 0 },
};

export default PurchaseRequestPage;
